import QuickViewWorkOrder from "components/QuickViewWorkOrder/QuickViewWorkOrder";
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
import { truncateString } from "utils/UtilityFuncs";
import AddProductsToShipment from "../AddProductsToShipment";

export default function ViewWoByCus({
  selectedCustomer,
  activeFilter,
  shipment,
}) {
  /*   All States
   ********************************************* */
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [totalSearchResult, setTotalSearchResult] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [showQuickView, setShowQuickView] = useState(false);
  const [selectedWo, setSelectedWo] = useState(null);
  const [searchText, setSearchText] = useState(query || "");

  // Simple nav items for label display
  const navItems = [
    { key: "all", label: "All" },
    { key: "pending", label: "Pending" },
    { key: "approved", label: "Approved" },
    { key: "processing", label: "Processing" },
    { key: "completed", label: "Completed" },
  ];

  /*   All Functions
   ********************************************* */

  // Search function
  const handleSearch = async (opts = {}) => {
    try {
      setLoading(true);
      const pageToUse = opts.page || 1;
      let body = {
        status: activeFilter,
        customerId: selectedCustomer?.customerId || undefined,
        page: pageToUse,
        limit,
        query,
        searchFields: ["woId", "customerName", "jobName"],
      };
      const { data } = await API.post(
        "/work-orders/shipment/search-pagination",
        body,
        {
          headers: authHeader(),
        }
      );
      if (data?.success) {
        setSearchResults(data?.workOrders || []);
        setTotalSearchResult(data?.total || 0);
        setPage(pageToUse);
      } else {
        setSearchResults([]);
        setTotalSearchResult(0);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  };

  // Load more function
  const handleLoadMore = async () => {
    try {
      setLoading(true);
      const nextPage = page + 1;
      let body = {
        status: activeFilter,
        customerId: selectedCustomer?.customerId || undefined,
        page: nextPage,
        limit,
        query,
        searchFields: ["woId", "customerName", "jobName"],
      };
      const { data } = await API.post(
        "/work-orders/shipment/search-pagination",
        body,
        {
          headers: authHeader(),
        }
      );
      if (data?.success) {
        setSearchResults((prev) => [
          ...(prev || []),
          ...(data?.workOrders || []),
        ]);
        setTotalSearchResult(data?.total || 0);
        setPage(nextPage);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  };

  const updateSearchParams = (key, value) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);

      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }

      return newParams.toString();
    });
  };

  // Trigger search when relevant inputs change
  useEffect(() => {
    // reset to first page when key filters change
    setPage(1);
    handleSearch({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer?.id, activeFilter, limit, query]);

  // Debounce input -> update query which triggers search effect
  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(searchText);
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  /*   All UseEffects
   ********************************************* */
  return (
    <div>
      <h4>
        Showing Work Orders for
        <span className="text-primary fw-bold ms-1">
          {selectedCustomer?.customerName || "N/A"}
        </span>
      </h4>
      <hr className="mb-1" />
      <div className="d-flex align-items-center justify-content-between mb-2 p-2">
        <div className="d-flex align-items-center gap-3">
          <label
            className="form-label text-nowrap mb-0 fw-bold"
            style={{ fontSize: "1rem" }}
          >
            Results per page
          </label>
          <select
            className="form-select form-select-sm w-auto"
            value={limit}
            onChange={(e) => {
              setLimit(parseInt(e.target.value));
              setPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
          <span
            className="fw-bold"
            style={{ color: "#1976d2", fontSize: "1.1rem" }}
          >
            {navItems.find((n) => n.key === activeFilter)?.label}
          </span>
        </div>
        <div className="d-flex align-items-center gap-2">
          <div className="input-group input-group-sm">
            <input
              type="search"
              className="form-control"
              placeholder="Search work orders..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setQuery(searchText);
                  handleSearch({ page: 1 });
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline-danger"
              onClick={() => {
                setSearchText("");
                setQuery("");
                handleSearch({ page: 1 });
              }}
            >
              Clear
            </button>
          </div>

          {(searchResults || []).length > 0 && (
            <span className="text-muted text-nowrap small ms-1">
              Showing <b>{(searchResults || []).length}</b> of{" "}
              <b>{totalSearchResult}</b>
            </span>
          )}
        </div>
      </div>
      {loading && (
        <div className="my-3 d-flex justify-content-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
      {(searchResults || []).length > 0 && (
        <div
          className="border-3 mb-2"
          style={{ maxHeight: "77vh", overflowY: "auto" }}
        >
          <div className="p-0">
            <table className="table mb-0">
              <thead
                className="table-light"
                style={{ position: "sticky", top: 0, zIndex: 1 }}
              >
                <tr>
                  <th style={{ width: "5%" }}>ID</th>
                  <th style={{ width: "35%" }}>Customer</th>
                  <th style={{ width: "50%" }}>Job Name</th>
                  <th style={{ width: "10%" }}>Action</th>
                </tr>
              </thead>
              <tbody className="">
                {(searchResults || []).map((wo) => (
                  <tr key={wo.woId} className="table-row-hover">
                    <td className="align-middle py-1">{wo.woId}</td>
                    <td className="align-middle py-1">
                      {truncateString(wo.customerName, 40)}
                    </td>
                    <td className="align-middle py-1">
                      {truncateString(wo.jobName, 40)}
                    </td>
                    <td className="align-middle py-1 d-flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // Handle view action here
                          updateSearchParams("selected-work-order", wo.woId);
                          setShowQuickView(true);
                        }}
                        className="btn btn-sm btn-outline-secondary"
                      >
                        View
                      </button>
                      <AddProductsToShipment wo={wo} shipment={shipment} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalSearchResult > (searchResults || []).length && (
              <div className="p-2 text-center bg-light">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleLoadMore}
                  disabled={loading}
                >
                  Load More
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {(searchResults || []).length === 0 && !loading && (
        <div className="alert alert-info mt-3">No work orders found</div>
      )}
      <QuickViewWorkOrder
        workOrdersList={searchResults}
        active={activeFilter}
        btnText="View"
        show={showQuickView}
        setShow={setShowQuickView}
        btnClasses={"btn btn-sm btn-outline-secondary"}
        refreshFunc={handleSearch}
        updateSearchParams={updateSearchParams}
      />
    </div>
  );
}
